import type { ReactNode } from "react";
import { ScrollView, Pressable, StyleSheet, Text, View } from "react-native";

import { webTapFloor, type UiTheme } from "../../theme";

// ChipStrip — the reusable horizontal chip switcher chrome lifted out of the
// logger's RosterStrip (the "questo roster lo riutilizzeremo di sicuro"
// decision, 2026-06-04). It owns the shared parts: a section label + hint,
// an expand/collapse chevron, and the expanded-grid / collapsed-scroll
// layout. ≥44pt targets, shape-not-colour-only state — reduced-vision safe.
//
// Lifted to @heraion/ui (S3, 2026-06-29) with MeasurementTrends so the web
// console gets the same trend metric picker; behaviour unchanged (theme is the
// only swap: ReturnType<typeof useTheme> → UiTheme). The mobile logger's
// RosterStrip imports it from here too.
//
// Two ways to use it:
//  - `children`: render your own rich chips (the logger does this — its
//    chips carry a state dot + set count + superset tag). Behaviour-preserving.
//  - `options` + `mode`: the built-in selectable chips. `mode: 'single'`
//    (default — exactly one active, like a switcher) or `'multi'` (toggle
//    several, e.g. the body-measurement trend metric picker). Controlled:
//    the parent owns `selectedKeys` and reacts to `onSelect(key)`.
//
// `leadingSlot` (PE-22 fetta E) is chrome, not a chip: it renders in a fixed
// cell BESIDE the scroller, never inside it. The strip's ＋ had nowhere to
// live that survived: in the tail it leaves the screen as soon as the chips
// overflow, and anywhere inside the scroll it leaves as soon as you scroll.
// Only a cell outside the ScrollView is always reachable.

export type ChipOption = { key: string; label: string };

type CommonProps = {
  label: string;
  hint?: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  expandA11y: string;
  collapseA11y: string;
  /** Fixed leading cell, rendered outside the horizontal scroll — see the
   *  note above. Pass a primitive (an <IconButton>), never a hand-rolled
   *  Pressable: the slot is a layout cell, it does not style its occupant. */
  leadingSlot?: ReactNode;
  /** Show the expand/collapse chevron. Pass false when the strip has nothing
   *  to expand — with zero chips the chevron is a control whose action has no
   *  effect, which an assistive technology has no way to know. */
  collapsible?: boolean;
  /** Fixed cell at the end of the header row, before the chevron (the `?`
   *  pill of a chart card, #129). */
  headerRight?: ReactNode;
  theme: UiTheme;
};

type ChildrenProps = CommonProps & {
  children: ReactNode;
  options?: never;
};

type OptionsProps = CommonProps & {
  options: ChipOption[];
  mode?: "single" | "multi";
  selectedKeys: string[];
  onSelect: (key: string) => void;
  children?: never;
};

type Props = ChildrenProps | OptionsProps;

export function ChipStrip(props: Props) {
  const {
    label,
    hint,
    expanded,
    onToggleExpanded,
    leadingSlot,
    collapsible = true,
    headerRight,
    theme,
  } = props;
  const styles = makeStyles(theme);

  const body =
    "children" in props && props.children !== undefined ? (
      props.children
    ) : "options" in props && props.options ? (
      props.options.map((opt) => {
        const active = props.selectedKeys.includes(opt.key);
        return (
          <Pressable
            key={opt.key}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => props.onSelect(opt.key)}
            hitSlop={8}
            style={({ pressed }) => [
              styles.chip,
              active && styles.chipActive,
              pressed && styles.chipPressed,
            ]}
          >
            {/* ✓ prefix, not colour alone, marks the active chip
                ([[feedback_user_visual_accessibility]]). */}
            <Text
              style={[styles.chipLabel, active && styles.chipLabelActive]}
              numberOfLines={1}
            >
              {active ? "✓ " : ""}
              {opt.label}
            </Text>
          </Pressable>
        );
      })
    ) : null;

  return (
    <View style={styles.wrap}>
      <View style={styles.header}>
        {/* One line, then ellipsis. The label owns `flex: 1` and is the only
            shrinkable child, so without this a long section label or a long
            control beside it wraps the header onto two rows. */}
        <Text style={styles.label} numberOfLines={1}>
          {label}
          {hint ? (
            <Text style={styles.hint}>
              {"  "}
              {hint}
            </Text>
          ) : null}
        </Text>
        {/* TODO(cleanup): this is a hand-rolled 36-box, not <IconButton> —
            pre-existing drift, flagged by the fetta-E scout. Swapping it
            changes the chevron's look (IconButton carries a border + surface2
            fill), so it is a design call, not a refactor. */}
        {headerRight}
        {collapsible ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={expanded ? props.collapseA11y : props.expandA11y}
            onPress={onToggleExpanded}
            hitSlop={8}
            style={({ pressed }) => [styles.chevron, pressed && styles.chevronPressed]}
          >
            <Text style={styles.chevronText}>{expanded ? "▴" : "▾"}</Text>
          </Pressable>
        ) : null}
      </View>
      <View style={styles.stripRow}>
        {leadingSlot ? (
          <View style={styles.leadingCell}>{leadingSlot}</View>
        ) : null}
        {expanded ? (
          <View style={[styles.grid, styles.scrollCell]}>{body}</View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.scrollCell}
            contentContainerStyle={styles.strip}
            keyboardShouldPersistTaps="handled"
          >
            {body}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s1 },
    // The fixed cell and the scroller sit side by side; only the scroller
    // gives ground. `minWidth: 0` is what stops it: a flex child sizes to its
    // content by default, so on web an overflowing row of chips pushes the
    // leading cell off-screen — the very failure this slot exists to prevent.
    // gap s3, not s2. The occupant and the first chip both carry a hitSlop of
    // 8, so at 4pt of gap their touch zones would have sat on the same pixels;
    // at 8 they meet exactly on the gap and neither reaches past it. Same
    // arithmetic SupersetTabs and CuePill already use.
    // ⚠️ `flex-start`, non `center` (Antenore 27/08, sullo smoke di 4.29.0-dev:
    // «quando gli esercizi sono espansi il più è al centro della lista, un po'
    // bruttino da vedere»). Da COLLASSATA la riga è alta un chip e il centro
    // coincideva col posto giusto, quindi il difetto non si vedeva; da ESPANSA
    // la griglia è alta tre righe e l'occupante galleggiava a metà, in una
    // colonna sua.
    // ⛔ La via che sembrava più pulita — mettere l'occupante DENTRO la griglia,
    // come primo elemento del flusso — è stata scartata misurando: la griglia ha
    // gap `s2` (4pt) contro gli `s3` (8) di questa riga, e a 4pt le zone di
    // tocco dell'occupante e del primo chip tornerebbero a sovrapporsi. È
    // esattamente l'aritmetica che la fetta E aveva sistemato.
    stripRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: s.s3,
    },
    // Lo stesso `paddingVertical` che griglia e striscia danno ai chip, così
    // l'occupante si allinea col PRIMO chip e non col bordo della riga. Senza,
    // `flex-start` lo alzerebbe di 2pt e la cura si vedrebbe come un difetto.
    leadingCell: { paddingVertical: s.s1 },
    scrollCell: { flex: 1, minWidth: 0 },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s2,
    },
    label: {
      flex: 1,
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    hint: {
      color: p.ink3,
      fontWeight: "500",
      textTransform: "none",
      letterSpacing: 0,
    },
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s2,
      paddingVertical: s.s1,
    },
    strip: { gap: s.s2, paddingVertical: s.s1 },
    chevron: {
      // DS-58: same idiom as the `chip` floor below (webTapFloor, 44 on web,
      // undefined on native, so the shipped native size is untouched).
      width: webTapFloor ?? 36,
      height: webTapFloor ?? 36,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: r.r2,
    },
    chevronPressed: { backgroundColor: p.surface2 },
    chevronText: { color: p.ink2, fontSize: ty.sizes.md, fontWeight: "700" },
    // Built-in selectable chip (options API). Rich custom chips come in via
    // `children` instead and bring their own styles.
    chip: {
      maxWidth: 220,
      // DS-58: webTapFloor (44 on web, undefined on native) is RosterStrip's
      // fix for the same RNW gap (no hitSlop there, so the box IS the
      // target); this chip had been left at 36 when PE-22 E raised the
      // roster's.
      minHeight: webTapFloor ?? 36,
      minWidth: webTapFloor ?? 36,
      flexDirection: "row",
      alignItems: "center",
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s1,
      backgroundColor: p.surface,
    },
    chipActive: {
      borderColor: p.brand500,
      borderWidth: 2,
      backgroundColor: p.brand100,
    },
    chipPressed: { backgroundColor: p.surface2 },
    chipLabel: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
    },
    chipLabelActive: { color: p.brandText },
  });
}
